import { useEffect, useState } from "react";
import { useLang } from "../../i18n/LangContext";
import { api, fileUrl } from "../../lib/api";
import { loadPublicSettings } from "../../lib/publicSettings";

const Mark = ({ i }) => (
  <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
    <path d="M16 2 L28 9 V23 L16 30 L4 23 V9 Z" />
    {i % 3 === 0 && <circle cx="16" cy="16" r="5" />}
    {i % 3 === 1 && <path d="M10 16 h12 M16 10 v12" />}
    {i % 3 === 2 && <path d="M10 20 L16 11 L22 20 Z" />}
  </svg>
);

const Placeholder = ({ name, i, hidden }) => {
  const { t } = useLang();
  return (
    <div className="partner-slot" aria-hidden={hidden} data-testid={hidden ? undefined : `partner-slot-${i}`}>
      <Mark i={i} />
      <span><small>{t.partners.slot}</small><strong>{name}</strong></span>
    </div>
  );
};

const PartnerCard = ({ p, hidden }) => {
  const inner = p.logo_url
    ? <img src={fileUrl(p.logo_url)} alt={p.name} loading="lazy" />
    : <strong>{p.name}</strong>;
  const props = { className: "partner-slot partner-real", "aria-hidden": hidden, "data-testid": hidden ? undefined : `partner-card-${p.id}`, title: p.name };
  return p.link
    ? <a href={p.link} target="_blank" rel="noopener noreferrer" tabIndex={hidden ? -1 : undefined} {...props}>{inner}</a>
    : <div {...props}>{inner}</div>;
};

export const Partners = () => {
  const { t } = useLang();
  const [partners, setPartners] = useState([]);
  useEffect(() => { api.get("/partners").then((r) => setPartners(r.data)).catch(() => setPartners([])); }, []);

  let row;
  if (partners.length) {
    const reps = Math.max(1, Math.ceil(8 / partners.length));
    const base = Array.from({ length: reps }, () => partners).flat();
    row = [...base, ...base].map((p, i) => <PartnerCard key={i} p={p} hidden={i >= partners.length} />);
  } else {
    const slots = [...t.partners.slots, ...t.partners.slots];
    row = slots.map((name, i) => <Placeholder key={i} name={name} i={i} hidden={i >= t.partners.slots.length} />);
  }
  return (
    <section className="partners" aria-label={t.partners.head} data-testid="partners-section">
      <div className="wrap">
        <p className="partners-head">{t.partners.head}</p>
        <div className="partners-marquee"><div className="partners-track">{row}</div></div>
      </div>
    </section>
  );
};

export const KeyFigures = () => {
  const { t, lang } = useLang();
  const [experts, setExperts] = useState(null);
  const [custom, setCustom] = useState(null);
  useEffect(() => {
    api.get("/stats").then((r) => setExperts(r.data.experts)).catch(() => setExperts(null));
    loadPublicSettings().then((s) => setCustom(s.figures));
  }, []);
  const items = custom?.length
    ? custom.map((f) => [f.live ? "live" : f[`value_${lang}`], f[`label_${lang}`]])
    : t.figures.items;
  return (
    <section className="section figures" style={{ paddingTop: 64, paddingBottom: 0 }} aria-label={t.figures.eyebrow} data-testid="key-figures-section">
      <div className="wrap">
        <div className="figures-strip">
          <div className="figures-label"><p className="eyebrow">{t.figures.eyebrow}</p></div>
          {items.map(([v, l], i) => (
            <div className="figure" key={i}>
              <b data-testid={`key-figure-${i}`}>
                {v === "live" ? <>{experts ?? "—"}<span className="live-dot" aria-hidden="true" /></> : v}
              </b>
              <span>{l}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
